import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { USE_CASES } from "../data/useCases";

const slugToTKey: Record<string, string> = {
  "sales-team": "salesTeam",
  "customer-support": "customerSupport",
  "content-creator-media": "contentCreatorMedia",
  "hr-recruitment": "hrRecruitment",
  "team-meeting-task-automation": "teamMeetingTaskAutomation",
  "marketing-project": "marketingProject",
};

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const item = {
  hidden: { y: 24, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const UseCases: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="px-4 md:px-0 pt-6 md:pt-8 pb-8 bg-[#f8f8f8] md:bg-transparent">
      {/* Breadcrumb */}
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-[#494949] text-sm mb-4 md:mb-6"
        aria-label="Breadcrumb"
      >
        <Link to="/use-cases" className="df-color hover:underline font-light">
          {t("useCases.breadcrumb")}
        </Link>
        <span className="mx-1">&gt;</span>
      </motion.nav>

      {/* Title & Subtitle — căn giữa */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="text-center mb-8 md:mb-12"
      >
        <h1 className="text-[#494949] text-3xl md:text-5xl font-bold leading-tight mb-2 md:mb-3">
          {t("useCases.pageTitle")}
        </h1>
        <p className="text-[#888888] text-base md:text-xl">
          {t("useCases.pageSubtitle")}
        </p>
      </motion.div>

      {/* Mobile: 1 cột dọc, bo tròn chỉ card đầu (trên) và card cuối (dưới). Desktop: grid 2x3, bo 4 góc. Click = chuyển hướng, không cần hiệu ứng. */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6"
      >
        {USE_CASES.map((uc, index) => {
          // Mobile: chỉ card đầu (1) bo góc trên, card cuối (6) bo góc dưới. Desktop: 4 góc grid như cũ.
          const radiusClass =
            index === 0
              ? "max-md:rounded-t-2xl md:rounded-tl-2xl"
              : index === 1
                ? "md:rounded-tr-2xl"
                : index === 4
                  ? "md:rounded-bl-2xl"
                  : index === 5
                    ? "max-md:rounded-b-2xl md:rounded-br-2xl"
                    : "";
          return (
            <motion.div key={uc.slug} variants={item}>
              <Link
                to={`/use-cases/${uc.slug}`}
                className={`group block relative isolate [contain:paint] overflow-hidden bg-[#2a2a2a] aspect-[16/10] md:aspect-[780/384] ${radiusClass}`}
              >
                {/* Ảnh: desktop hover scale; mobile không hiệu ứng (chỉ click chuyển hướng) */}
                <div
                  className="absolute inset-0 transform-gpu will-change-transform bg-cover bg-center transition-transform duration-[800ms] ease-out md:group-hover:scale-[1.2]"
                  style={{
                    backgroundImage: uc.image
                      ? `url("${uc.image}")`
                      : "linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 100%)",
                    borderRadius: "inherit",
                  }}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 transition-opacity duration-300 md:group-hover:opacity-0 max-md:hidden"
                  style={{ borderRadius: "inherit" }}
                  aria-hidden
                />
                {/* Nội dung: overlay đáy card, gradient + blur */}
                <div
                  className="absolute left-0 right-0 bottom-0 overflow-hidden flex flex-col items-center justify-end px-4 py-4 text-white text-center"
                  style={{
                    background:
                      "linear-gradient(360deg, rgba(0, 0, 0, 0.36) 26.23%, rgba(0, 0, 0, 0) 86.45%)",
                    backdropFilter: "blur(14.5px)",
                    WebkitBackdropFilter: "blur(14.5px)",
                    clipPath: "inset(0)",
                    borderRadius: "inherit",
                  }}
                >
                  <h2 className="text-lg md:text-xl font-bold mb-1 drop-shadow-md">
                    {t(`useCases.${slugToTKey[uc.slug]}.title`)}
                  </h2>
                  <p className="text-white/95 text-sm drop-shadow-md">
                    {t(`useCases.${slugToTKey[uc.slug]}.subtitle`)}
                  </p>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default UseCases;
